import { values } from 'lodash';
import React, { useState } from 'react';
import { Size } from '../../../Enums/Size.enum';
import { HealthPlanUser } from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import { useHealthPlanContext } from '../../../Services/HealthPlanContext';
import { useTreatmentContext } from '../../../Services/TreatmentContext';
import Modal from '../../Modal/Modal';
import HealthPlanWrapper from './HealthPlanWrapper';
import Loading from '../../Loading';
import { getHealthPlanTimeline } from '../../../Modules/HealthPlan/Services/healthPlan.service';
import useScope from '../../../Modules/Scope/Hooks/useScope';

export interface HealthPlanWrapperContainerProps {
	patientId?: string;
	onSuccess?: () => void;
	showContinue?: boolean;
	modalTitle?: string;
	modalExtraText?: string[];
	modalDescription?: string;
	modalImages?: string[];
	modalItems?: number;
	modalSubtitle?: string;
	modalItemPrefix: string;
	modalComponent?: React.ElementType;
	wrapperCollapseTitle?: string;
	wrapperCollapse?: React.ElementType;
	setIsLoading: (isLoading: boolean) => void;
	isLoading?: boolean;
}

interface Props extends HealthPlanWrapperContainerProps {
	healthPlanUser: HealthPlanUser;
}

const HealthPlanWrapperContainer: React.FC<Props> = ({
	patientId,
	onSuccess,
	showContinue = true,
	modalTitle,
	modalExtraText,
	modalDescription,
	modalItems,
	modalItemPrefix,
	modalSubtitle,
	modalComponent: ModalComponent,
	wrapperCollapseTitle,
	wrapperCollapse,
	setIsLoading,
	modalImages,
	healthPlanUser,
}) => {
	const { healthPlanData, setHealthPlanData } = useHealthPlanContext();
	const { isCase } = useTreatmentContext();
	const [infoModalOpen, setInfoModalOpen] = useState(true);
	const [isLoadingInner, setIsLoadingInner] = useState(true);
	const { isLatam } = useScope();

	useEffectAsync(async () => {
		setIsLoadingInner(true);
		if (healthPlanUser?.healthPlanId && healthPlanUser?.id) {
			const { data: healthPlanData } = await getHealthPlanTimeline({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
			});
			setHealthPlanData(healthPlanData);
		}
		setIsLoadingInner(false);
	}, [healthPlanUser]);

	if (isLoadingInner) {
		return <Loading />;
	}

	const shouldRenderModal =
		isLatam &&
		modalTitle !== undefined &&
		modalItems !== undefined &&
		ModalComponent !== undefined &&
		!healthPlanUser?.ready;

	return (
		<>
			<HealthPlanWrapper
				allowEdit
				showDetails
				showContinue={showContinue}
				healthPlanUser={healthPlanUser}
				data={values(healthPlanData)}
				patientId={patientId}
				onSuccess={onSuccess}
				wrapperCollapseTitle={wrapperCollapseTitle}
				wrapperCollapse={wrapperCollapse}
				setIsLoading={setIsLoading}
			/>
			{shouldRenderModal && (
				<Modal
					opened={infoModalOpen}
					onClose={() => setInfoModalOpen(false)}
					wrapChat={isCase}
					size={Size.XL}>
					<Modal.Header title={modalTitle} />
					<Modal.Content contentScroll>
						<ModalComponent
							items={modalItems}
							description={modalDescription}
							modalItemPrefix={modalItemPrefix}
							modalExtraText={modalExtraText}
							subtitle={modalSubtitle}
							modalImages={modalImages}
						/>
					</Modal.Content>
				</Modal>
			)}
		</>
	);
};

export default HealthPlanWrapperContainer;
